import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { BarChart3, CalendarCheck, CalendarDays, CreditCard, FileText, LogOut, Mail, MailOpen, MessageSquareText, Music as MusicIcon, ShoppingBag, Settings as Cog, Sparkles, Star, Users, Images, Contact, UserCog, CalendarClock, UsersRound } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TOKEN_KEY, api } from "@/lib/api";
import Staff from "./Staff";
import MyCalendar from "./MyCalendar";
import TeamAdmin from "./TeamAdmin";
import { StaffLogin } from "./Login";
import Dashboard from "./Dashboard";
import Bookings from "./Bookings";
import Calendar from "./Calendar";
import Revenue from "./Revenue";
import Messages from "./Messages";
import Subscribers from "./Subscribers";
import Customers from "./Customers";
import Popups from "./Popups";
import ContentEditor from "./ContentEditor";
import Emails from "./Emails";
import ServicesEditor from "./ServicesEditor";
import Settings from "./Settings";
import Music from "./Music";
import ShopEditor from "./ShopEditor";
import WorkAdmin from "./WorkAdmin";
import ReviewsAdmin from "./ReviewsAdmin";
import { FeatureOff } from "./Features";
import { useFeatures } from "@/lib/features";
import "./admin.css";

const NAV = [
  ["", "Dashboard", BarChart3], ["my-calendar", "My calendar", CalendarClock], ["calendar", "Calendar", CalendarDays], ["team", "Team & schedules", UsersRound], ["bookings", "Bookings", CalendarCheck], ["revenue", "Revenue", CreditCard], ["customers", "Customers", Contact],
  ["messages", "Messages", Mail], ["subscribers", "Subscribers", Users], ["popups", "Pop-ups", MessageSquareText],
  ["content", "Site content", FileText], ["emails", "Emails", MailOpen], ["work", "Our work", Images], ["reviews", "Reviews", Star], ["music", "Music", MusicIcon], ["shop", "Shop items", ShoppingBag], ["services", "Services & prices", Sparkles], ["settings", "Settings", Cog],
];

const PAGES = {
  dashboard: <Dashboard />, "my-calendar": <MyCalendar />, calendar: <Calendar />, team: <TeamAdmin />, bookings: <Bookings />, customers: <Customers />, messages: <Messages />,
  subscribers: <Subscribers />, popups: <Popups />, content: <ContentEditor />, emails: true, work: <WorkAdmin />, reviews: <ReviewsAdmin />, music: <Music />,
  shop: <ShopEditor />, services: <ServicesEditor />, settings: <Settings />,
};
const REVENUE_TABS = ["payments", "shop-sales"];
const EMPTY_ME = { tabs: [], is_owner: false };
const TAB_FEATURE = { calendar: "calendar", "my-calendar": "my-calendar", team: "team", bookings: "bookings", customers: "customers", messages: "messages",
  work: "work", reviews: "reviews", popups: "popups", music: "music", shop: "shop" };
const offKey = (f, to) => {
  if (!f) return null;
  if (to === "revenue") return f.payments === false && f.shop === false ? "payments" : null;
  const k = TAB_FEATURE[to];
  return k && f[k] === false ? k : null;
};
const allowed = (me, to) => (to === "my-calendar" ? !!me.staff_member_id && me.tabs.includes(to) : to === "revenue" ? REVENUE_TABS.some((t) => me.tabs.includes(t)) : me.tabs.includes(to || "dashboard"));

const useMe = () => useQuery({ queryKey: ["me"], queryFn: () => api.get("/auth/me").then((r) => r.data), staleTime: 60_000 }).data;

const useAdminNav = (me) => useMemo(() => {
  const nav = [...NAV.filter(([to]) => allowed(me, to)), ...(me.is_owner ? [["staff", "Staff logins", UserCog]] : [])];
  return { nav, home: nav[0] ? `/admin/${nav[0][0]}` : null };
}, [me]);

const SideNav = ({ me, nav: all, f, onLogout }) => { const nav = all.filter(([to]) => !offKey(f, to)); return (
  <aside className="ad-side">
    <a href="/" className="ad-logo" data-testid="admin-site-link"><img src="/brand/logo-gold.png" alt="Wax Moi" width="835" height="240" /></a>
    <p className="ad-who" data-testid="admin-current-user">{me.name} · {me.is_owner ? "Owner" : "Staff"}</p>
    <nav className="ad-nav" data-testid="admin-nav">
      {nav.map(([to, label, Icon]) => (
        <NavLink key={to} end={to === ""} to={`/admin/${to}`} className="ad-nav-link" data-testid={`admin-nav-${to || "dashboard"}`}>
          <Icon size={16} /> {label}
        </NavLink>
      ))}
    </nav>
    <button className="ad-logout" onClick={onLogout} data-testid="admin-logout-button"><LogOut size={15} /> Log out</button>
  </aside>
); };

const AdminRoutes = ({ me, f, home }) => {
  const page = (t) => (offKey(f, t) ? <FeatureOff k={offKey(f, t)} isOwner={me.is_owner} /> : t === "emails" ? <Emails me={me} /> : PAGES[t]);
  return (
    <Routes>
      {me.tabs.includes("dashboard") ? <Route index element={PAGES.dashboard} /> : <Route index element={<Navigate to={home} replace />} />}
      {me.tabs.filter((t) => PAGES[t] && t !== "dashboard").map((t) => <Route key={t} path={t} element={page(t)} />)}
      {allowed(me, "revenue") && <Route path="revenue/*" element={<Revenue tabs={me.tabs} features={f} isOwner={me.is_owner} />} />}
      {me.tabs.includes("payments") && <Route path="payments" element={<Navigate to="/admin/revenue/services" replace />} />}
      {me.tabs.includes("shop-sales") && <Route path="shop-sales" element={<Navigate to="/admin/revenue/shop" replace />} />}
      {me.is_owner && <Route path="staff" element={<Staff me={me} />} />}
      <Route path="*" element={<Navigate to={home} replace />} />
    </Routes>
  );
};

const Shell = ({ onLogout }) => {
  const me = useMe();
  const f = useFeatures();
  const { nav, home } = useAdminNav(me || EMPTY_ME);
  if (!me) return <p className="muted-sm ad-loading" data-testid="admin-loading">Loading…</p>;
  return (
    <div className="ad-shell">
      <SideNav me={me} nav={nav} f={f} onLogout={onLogout} />
      <main className="ad-main">
        {!home ? <p className="empty" data-testid="admin-no-access">Your login doesn't have access to any sections yet. Please ask the spa owner.</p> : <AdminRoutes me={me} f={f} home={home} />}
      </main>
    </div>
  );
};

const useAdminSession = () => {
  const [authed, setAuthed] = useState(() => !!localStorage.getItem(TOKEN_KEY));
  const qc = useQueryClient();
  const logout = useCallback(() => { localStorage.removeItem(TOKEN_KEY); qc.clear(); setAuthed(false); }, [qc]);
  const login = useCallback(() => { qc.clear(); setAuthed(true); }, [qc]);
  useEffect(() => {
    window.addEventListener("wm-logout", logout);
    return () => window.removeEventListener("wm-logout", logout);
  }, [logout]);
  return { authed, login, logout };
};

export default function Admin() {
  const { authed, login, logout } = useAdminSession();
  return <div className="wm admin">{authed ? <Shell onLogout={logout} /> : <StaffLogin onLogin={login} />}</div>;
}
